/**
 * Reading a bundle's response curves (plan R13). A curve holds the weekly outcome at each point of a weekly-spend grid
 * that starts at 0 and rises to G; these read it anywhere in between, and summarise per-draw values as a range. Pure.
 *
 * @module services/budget/curves
 */
import type { Ranged } from './bundleSchema';

const clamp = (x: number, top: number): number => Math.min(Math.max(x, 0), top);

/** The curve at weekly spend x: linear between grid points, x clamped to [0, G]. */
export const interpolate = (grid: readonly number[], ys: readonly number[], x: number): number => {
  const at = clamp(x, grid[grid.length - 1]);
  const j = grid.findIndex((g) => g >= at);
  if (j <= 0) return ys[0];
  const t = (at - grid[j - 1]) / (grid[j] - grid[j - 1]);
  // This form is exact at t = 0 and t = 1, so a grid point reads its own value.
  return (1 - t) * ys[j - 1] + t * ys[j];
};

/** The curve's slope at x (mROAS on a revenue curve): a central difference over one grid step, one-sided at the ends. */
export const slopeAt = (grid: readonly number[], ys: readonly number[], x: number): number => {
  // A one-point grid ([0], which the schema allows) has no step, so no slope.
  if (grid.length < 2) return 0;
  const top = grid[grid.length - 1];
  const h = grid[1] - grid[0];
  const at = clamp(x, top);
  const f = (v: number): number => interpolate(grid, ys, v);
  if (at - h >= 0 && at + h <= top) return (f(at + h) - f(at - h)) / (2 * h);
  if (at - h < 0) return (f(at + h) - f(at)) / h;
  return (f(at) - f(at - h)) / h;
};

/** The q-quantile of an ascending list, linear between ranks: numpy's default. */
export const quantile = (sorted: readonly number[], q: number): number => {
  // Only a bundle with no channels has no draws to range over, and every total of it is 0.
  if (sorted.length === 0) return 0;
  const pos = q * (sorted.length - 1);
  const i = Math.floor(pos);
  const next = sorted[Math.min(i + 1, sorted.length - 1)];
  return sorted[i] + (pos - i) * (next - sorted[i]);
};

/** The 5th–95th percentile range of per-draw values. */
export const rangeOf = (values: readonly number[]): { lo: number; hi: number } => {
  const sorted = [...values].sort((a, b) => a - b);
  return { lo: quantile(sorted, 0.05), hi: quantile(sorted, 0.95) };
};

/**
 * The mean with the 5th–95th range of per-draw values, widened to reach the mean: the mean is over every posterior
 * draw and the range over a sample of them, so the sample can miss it. Every Ranged the page computes comes from here,
 * so lo ≤ mean ≤ hi holds as it does in the bundle.
 */
export const rangedOf = (mean: number, values: readonly number[]): Ranged => {
  const { lo, hi } = rangeOf(values);
  return { mean, lo: Math.min(lo, mean), hi: Math.max(hi, mean) };
};
